import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, UploadSimple, FloppyDisk, Eye, ArrowLeft, ListChecks, PencilSimple, ImageSquare, SteeringWheel } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api, mediaUrl } from "../lib/api";

const inputCls = "w-full bg-ink border border-white/15 text-white px-3 py-2.5 text-sm outline-none focus:border-taxi transition-colors placeholder:text-white/30";
const labelCls = "block text-white/50 text-[11px] font-bold uppercase tracking-widest mb-1.5";

function ImageField({ label, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-4">
        <div className="h-20 w-28 bg-ink border border-white/15 overflow-hidden shrink-0 flex items-center justify-center">
          {value ? <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" /> : <ImageSquare size={24} className="text-white/20" />}
        </div>
        <label className="cursor-pointer border border-white/20 text-white/80 hover:border-taxi hover:text-taxi px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors">
          <UploadSimple size={16} /> {loading ? "..." : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      <input className={`${inputCls} mt-2`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" />
    </div>
  );
}

function Field({ label, value, onChange, textarea }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea rows="3" className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 bg-surface p-6 mb-6">
      <h3 className="font-display text-taxi text-2xl uppercase mb-5">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-ink grid-lines flex items-center justify-center px-5">
      <div className="grain" />
      <form onSubmit={submit} className="relative w-full max-w-md border border-white/10 bg-surface p-8 md:p-10" data-testid="admin-login-form">
        <img src="/mytaxi-logo.png" alt="MY TAXI" className="h-20 w-20 object-contain mx-auto mb-6" />
        <h1 className="font-display text-white text-4xl uppercase text-center mb-2">Admin Panel</h1>
        <p className="text-white/50 text-sm text-center mb-8">MY TAXI content manager</p>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email" />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="mt-7 w-full bg-taxi text-black font-bold py-3.5 rounded-full hover:bg-white transition-colors disabled:opacity-60" data-testid="admin-login-btn">
          {busy ? "..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tab, setTab] = useState("content");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      api.get("/content").then((r) => setContent(r.data));
      api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
      api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null) return <div className="min-h-screen bg-ink flex items-center justify-center"><div className="font-display text-taxi text-3xl animate-pulse">MY TAXI</div></div>;
  if (!user) return <LoginScreen />;
  if (!content) return <div className="min-h-screen bg-ink flex items-center justify-center"><div className="text-taxi animate-pulse">Loading…</div></div>;

  const upd = (path, val) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = val;
      return next;
    });
  };

  const addTestimonial = () => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.testimonials) next.testimonials = [];
      next.testimonials.push({ name: "", location: "", rating: 5, text_en: "", text_es: "" });
      return next;
    });
  };

  const removeTestimonial = (idx) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.testimonials.splice(idx, 1);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Saved successfully");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hero = content.hero;

  return (
    <div className="min-h-screen bg-ink text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/mytaxi-logo.png" alt="MY TAXI" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl">MY <span className="text-taxi">TAXI</span> · Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="border border-white/20 hover:border-taxi hover:text-taxi px-3 py-2 text-sm font-bold flex items-center gap-2 transition-colors" data-testid="admin-view-site">
              <Eye size={16} /> <span className="hidden sm:inline">View Site</span>
            </button>
            <button onClick={save} disabled={saving} className="bg-taxi text-black px-4 py-2 text-sm font-bold rounded-full hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-60" data-testid="admin-save">
              <FloppyDisk size={16} weight="fill" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => logout()} className="text-white/60 hover:text-usared p-2 transition-colors" data-testid="admin-logout">
              <SignOut size={20} />
            </button>
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-5 flex gap-1">
          {[["content", "Content", PencilSimple], ["bookings", `Bookings (${bookings.length})`, ListChecks], ["drivers", `Drivers (${drivers.length})`, SteeringWheel]].map(([id, lbl, Icon]) => (
            <button key={id} onClick={() => setTab(id)} className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${tab === id ? "border-taxi text-taxi" : "border-transparent text-white/50 hover:text-white"}`} data-testid={`admin-tab-${id}`}>
              <Icon size={16} /> {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 py-8">
        {tab === "content" && (
          <>
            <Section title="Logo & Hero">
              <div className="md:col-span-2"><ImageField label="Logo" value={content.logo} onChange={(v) => upd(["logo"], v)} /></div>
              <Field label="Badge (EN)" value={hero.badge_en} onChange={(v) => upd(["hero", "badge_en"], v)} />
              <Field label="Badge (ES)" value={hero.badge_es} onChange={(v) => upd(["hero", "badge_es"], v)} />
              <Field label="Title lines EN (one per line)" textarea value={(hero.title_en || []).join("\n")} onChange={(v) => upd(["hero", "title_en"], v.split("\n"))} />
              <Field label="Title lines ES (one per line)" textarea value={(hero.title_es || []).join("\n")} onChange={(v) => upd(["hero", "title_es"], v.split("\n"))} />
              <Field label="Subtitle (EN)" textarea value={hero.subtitle_en} onChange={(v) => upd(["hero", "subtitle_en"], v)} />
              <Field label="Subtitle (ES)" textarea value={hero.subtitle_es} onChange={(v) => upd(["hero", "subtitle_es"], v)} />
              <div className="md:col-span-2"><ImageField label="Hero Background Image" value={hero.image} onChange={(v) => upd(["hero", "image"], v)} /></div>
            </Section>

            {content.services.map((s, i) => (
              <Section key={s.id} title={`Service · ${s.id}`}>
                <Field label="Title (EN)" value={s.title_en} onChange={(v) => upd(["services", i, "title_en"], v)} />
                <Field label="Title (ES)" value={s.title_es} onChange={(v) => upd(["services", i, "title_es"], v)} />
                <Field label="Description (EN)" textarea value={s.desc_en} onChange={(v) => upd(["services", i, "desc_en"], v)} />
                <Field label="Description (ES)" textarea value={s.desc_es} onChange={(v) => upd(["services", i, "desc_es"], v)} />
                <div className="md:col-span-2"><ImageField label="Image" value={s.image} onChange={(v) => upd(["services", i, "image"], v)} /></div>
              </Section>
            ))}

            <Section title="Coverage Areas">
              <div className="md:col-span-2">
                <Field label="Areas (one per line)" textarea value={(content.coverage || []).join("\n")} onChange={(v) => upd(["coverage"], v.split("\n").filter((x) => x.trim()))} />
              </div>
            </Section>

            <Section title="About">
              <Field label="Title (EN)" textarea value={content.about.title_en} onChange={(v) => upd(["about", "title_en"], v)} />
              <Field label="Title (ES)" textarea value={content.about.title_es} onChange={(v) => upd(["about", "title_es"], v)} />
              <Field label="Text (EN)" textarea value={content.about.text_en} onChange={(v) => upd(["about", "text_en"], v)} />
              <Field label="Text (ES)" textarea value={content.about.text_es} onChange={(v) => upd(["about", "text_es"], v)} />
              <div className="md:col-span-2"><ImageField label="About Image" value={content.about.image} onChange={(v) => upd(["about", "image"], v)} /></div>
            </Section>

            <Section title="Stats">
              {content.stats.map((st, i) => (
                <div key={i} className="grid grid-cols-3 gap-3 md:col-span-1">
                  <Field label="Value" value={st.value} onChange={(v) => upd(["stats", i, "value"], v)} />
                  <Field label="Label EN" value={st.label_en} onChange={(v) => upd(["stats", i, "label_en"], v)} />
                  <Field label="Label ES" value={st.label_es} onChange={(v) => upd(["stats", i, "label_es"], v)} />
                </div>
              ))}
            </Section>

            <Section title="Video">
              <div className="md:col-span-2">
                <label className={labelCls}>Video File</label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer border border-white/20 text-white/80 hover:border-taxi hover:text-taxi px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors">
                    <UploadSimple size={16} /> Upload video
                    <input type="file" accept="video/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      toast.info("Uploading video…");
                      try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); upd(["video", "url"], data.url); toast.success("Video uploaded"); }
                      catch { toast.error("Upload failed"); }
                    }} />
                  </label>
                  {content.video?.url && <span className="text-white/40 text-xs">Video set ✓</span>}
                </div>
                <input className={`${inputCls} mt-2`} value={content.video?.url || ""} onChange={(e) => upd(["video", "url"], e.target.value)} placeholder="or paste video URL (.mp4)" />
              </div>
              <Field label="Title (EN)" textarea value={content.video?.title_en} onChange={(v) => upd(["video", "title_en"], v)} />
              <Field label="Title (ES)" textarea value={content.video?.title_es} onChange={(v) => upd(["video", "title_es"], v)} />
              <Field label="Subtitle (EN)" textarea value={content.video?.subtitle_en} onChange={(v) => upd(["video", "subtitle_en"], v)} />
              <Field label="Subtitle (ES)" textarea value={content.video?.subtitle_es} onChange={(v) => upd(["video", "subtitle_es"], v)} />
            </Section>

            <div className="border border-white/10 bg-surface p-6 mb-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-taxi text-2xl uppercase">Customer Reviews</h3>
                <button onClick={addTestimonial} className="border border-taxi text-taxi hover:bg-taxi hover:text-black px-4 py-2 text-sm font-bold transition-colors" data-testid="admin-add-review">
                  + Add Review
                </button>
              </div>
              {(content.testimonials || []).map((r, i) => (
                <div key={i} className="border border-white/10 bg-ink p-5 mb-4" data-testid={`admin-review-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/50 text-xs font-bold uppercase tracking-widest">Review #{i + 1}</span>
                    <button onClick={() => removeTestimonial(i)} className="text-usared hover:underline text-xs font-bold" data-testid={`admin-remove-review-${i}`}>Remove</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Name" value={r.name} onChange={(v) => upd(["testimonials", i, "name"], v)} />
                    <Field label="Location" value={r.location} onChange={(v) => upd(["testimonials", i, "location"], v)} />
                    <div>
                      <label className={labelCls}>Rating (1-5)</label>
                      <select className={inputCls} value={r.rating || 5} onChange={(e) => upd(["testimonials", i, "rating"], parseInt(e.target.value))}>
                        {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
                      </select>
                    </div>
                    <div className="hidden md:block" />
                    <Field label="Review (EN)" textarea value={r.text_en} onChange={(v) => upd(["testimonials", i, "text_en"], v)} />
                    <Field label="Review (ES)" textarea value={r.text_es} onChange={(v) => upd(["testimonials", i, "text_es"], v)} />
                  </div>
                </div>
              ))}
            </div>

            <Section title="Contact">
              <Field label="Phone 1" value={content.contact.phone1} onChange={(v) => upd(["contact", "phone1"], v)} />
              <Field label="Phone 2" value={content.contact.phone2} onChange={(v) => upd(["contact", "phone2"], v)} />
              <Field label="WhatsApp (digits only)" value={content.contact.whatsapp} onChange={(v) => upd(["contact", "whatsapp"], v)} />
              <Field label="Email" value={content.contact.email} onChange={(v) => upd(["contact", "email"], v)} />
            </Section>

            <button onClick={save} disabled={saving} className="w-full bg-taxi text-black font-bold py-4 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              <FloppyDisk size={18} weight="fill" /> {saving ? "Saving…" : "Save All Changes"}
            </button>
          </>
        )}

        {tab === "bookings" && (
          <div className="border border-white/10 bg-surface" data-testid="admin-bookings">
            {bookings.length === 0 ? (
              <div className="p-12 text-center text-white/40">No bookings yet.</div>
            ) : (
              <div className="divide-y divide-white/10">
                {bookings.map((b) => (
                  <div key={b.id} className="p-5 hover:bg-ink/50 transition-colors">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-display text-taxi text-xl">{b.name}</span>
                      <span className="text-white/40 text-xs">{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-white/40">Phone: </span>{b.phone}</div>
                      <div><span className="text-white/40">Service: </span>{b.service}</div>
                      <div><span className="text-white/40">Pickup: </span>{b.pickup}</div>
                      <div><span className="text-white/40">Drop-off: </span>{b.dropoff}</div>
                      {b.date && <div><span className="text-white/40">Date: </span>{b.date} {b.time}</div>}
                      {b.passengers && <div><span className="text-white/40">Pax: </span>{b.passengers}</div>}
                      {b.notes && <div className="col-span-2 md:col-span-4"><span className="text-white/40">Notes: </span>{b.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "drivers" && (
          <div className="border border-white/10 bg-surface" data-testid="admin-drivers">
            {drivers.length === 0 ? (
              <div className="p-12 text-center text-white/40">No driver applications yet.</div>
            ) : (
              <div className="divide-y divide-white/10">
                {drivers.map((d) => (
                  <div key={d.id} className="p-5 hover:bg-ink/50 transition-colors">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-display text-taxi text-xl">{d.name}</span>
                      <span className="text-white/40 text-xs">{new Date(d.created_at).toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-white/40">Phone: </span>{d.phone}</div>
                      {d.email && <div><span className="text-white/40">Email: </span>{d.email}</div>}
                      {d.city && <div><span className="text-white/40">City: </span>{d.city}</div>}
                      {d.state && <div><span className="text-white/40">State: </span>{d.state}</div>}
                      {d.experience && <div><span className="text-white/40">Experience: </span>{d.experience}</div>}
                      {d.has_car && <div><span className="text-white/40">Own car: </span>{d.has_car}</div>}
                      {d.notes && <div className="col-span-2 md:col-span-4"><span className="text-white/40">Notes: </span>{d.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
